import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Form, Table, Spinner, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { FaWindowClose, FaSearch, FaPlus, FaTrash, FaEraser } from 'react-icons/fa';
import moment from 'moment';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  lookupGePorUsuario,
  lookupCidades,
  lookupContasPorGE,
  listarContasPrefeitura,
  incluirContaPrefeitura,
  excluirContaPrefeitura,
} from '~/Services/GestaoIPTUService';
import { extrairMensagemErro } from '~/Utils/Helpers';
import dialog from '~/Utils/dialog';

const schema = Yup.object({
  ge: Yup.number().required('GE é obrigatório.').nullable(),
  conta: Yup.number().required('Conta é obrigatória.').nullable(),
  cidade: Yup.number().required('Cidade é obrigatória.').nullable(),
});
const CIDADES_PAGE_SIZE = 50;

const ModalCadContaPrefeitura = ({ show, onHide }) => {
  const [filtroGe, setFiltroGe] = useState(null);
  const [filtroConta, setFiltroConta] = useState(null);
  const [filtroCidade, setFiltroCidade] = useState(null);
  const [contas, setContas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [mostrarFormIncluir, setMostrarFormIncluir] = useState(false);
  const [ges, setGes] = useState([]);
  const [cidadesVisiveis, setCidadesVisiveis] = useState([]);
  const [carregandoCidades, setCarregandoCidades] = useState(false);
  const [cidadeFiltroOpcao, setCidadeFiltroOpcao] = useState(null);
  const [cidadeInclusaoOpcao, setCidadeInclusaoOpcao] = useState(null);
  const [contasLookup, setContasLookup] = useState([]);
  const [contasFiltroLookup, setContasFiltroLookup] = useState([]);
  const cidadeTermoRef = useRef('');
  const cidadePageRef = useRef(1);
  const cidadeHasMoreRef = useRef(true);
  const cidadeRequestSeqRef = useRef(0);

  // GE (tabela grupo_empresa) — distinto de "Grupo GE" (Grupo_GE).
  // Replica cdGE.CommandText em uContaPrefeituraEmpreendimento.pas.
  const opcoesGe = (ges || []).map((g) => ({
    value: g.codigo_grupo_empresa,
    label: `${g.codigo_grupo_empresa} - ${g.apelido || g.descricao || ''}`,
  }));
  const opcoesCidade = (cidadesVisiveis || []).map((c) => ({
    value: c.codigo,
    label: `${c.codigo} - ${c.descricao}`,
  }));

  const carregarCidades = useCallback(async ({ termo = '', page = 1, append = false } = {}) => {
    const requestSeq = cidadeRequestSeqRef.current + 1;
    cidadeRequestSeqRef.current = requestSeq;
    setCarregandoCidades(true);
    try {
      const res = await lookupCidades({
        termo,
        page,
        page_size: CIDADES_PAGE_SIZE,
      });
      if (requestSeq !== cidadeRequestSeqRef.current) return;
      const lista = res || [];
      cidadePageRef.current = page;
      cidadeHasMoreRef.current = lista.length === CIDADES_PAGE_SIZE;
      setCidadesVisiveis((atuais) => (append ? [...atuais, ...lista] : lista));
    } finally {
      if (requestSeq === cidadeRequestSeqRef.current) {
        setCarregandoCidades(false);
      }
    }
  }, []);

  const handleCidadeMenuOpen = () => {
    if (cidadesVisiveis.length === 0) {
      carregarCidades({ termo: cidadeTermoRef.current, page: 1 });
    }
  };

  const handleCidadeSearch = (inputValue, actionMeta) => {
    if (actionMeta && actionMeta.action !== 'input-change') return inputValue;
    cidadeTermoRef.current = inputValue || '';
    cidadePageRef.current = 1;
    cidadeHasMoreRef.current = true;
    carregarCidades({ termo: inputValue || '', page: 1 });
    return inputValue;
  };

  const carregarMaisCidades = () => {
    if (!cidadeHasMoreRef.current || carregandoCidades) return;
    carregarCidades({
      termo: cidadeTermoRef.current,
      page: cidadePageRef.current + 1,
      append: true,
    });
  };

  useEffect(() => {
    if (!show) return;
    lookupGePorUsuario().catch(() => []).then((gesRes) => {
      setGes(gesRes || []);
    });
  }, [show]);

  // Lookup de contas pra filtro (depende do GE do filtro).
  useEffect(() => {
    if (!filtroGe) { setContasFiltroLookup([]); return; }
    lookupContasPorGE(filtroGe).then((res) => setContasFiltroLookup(res || []));
  }, [filtroGe]);

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = {};
      if (filtroGe)     params.ge = filtroGe;
      if (filtroConta)  params.conta = filtroConta;
      if (filtroCidade) params.cidade = filtroCidade;
      const res = await listarContasPrefeitura(params);
      setContas(res || []);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregando(false);
    }
  }, [filtroGe, filtroConta, filtroCidade]);

  useEffect(() => {
    if (show) buscar();
  }, [show, buscar]);

  const limpar = () => {
    setFiltroGe(null);
    setFiltroConta(null);
    setFiltroCidade(null);
    setCidadeFiltroOpcao(null);
    setCidadeInclusaoOpcao(null);
  };

  const handleExcluir = async (item) => {
    const result = await dialog.confirm({
      title: 'Excluir conta da prefeitura',
      message: `Deseja excluir a conta ${item.grupo_empresa}/${item.codigo_conta}-${item.descricao_conta}?`,
    });
    if (!result.proceed) return;
    try {
      await excluirContaPrefeitura({ grupo_empresa: item.grupo_empresa, codigo_conta: item.codigo_conta });
      toast.success('Conta excluída.');
      buscar();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  const handleIncluir = async (values, { setSubmitting, resetForm }) => {
    const result = await dialog.confirm({
      title: 'Cadastrar conta da prefeitura',
      message: 'Deseja cadastrar nova conta?',
    });
    if (!result.proceed) {
      setSubmitting(false);
      return;
    }
    try {
      await incluirContaPrefeitura({
        grupo_empresa: values.ge,
        codigo_conta: values.conta,
        codigo_cidade: values.cidade,
      });
      toast.success('Conta cadastrada com sucesso.');
      resetForm();
      setCidadeInclusaoOpcao(null);
      setMostrarFormIncluir(false);
      buscar();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>
          Cadastro de Conta da Prefeitura por Empreendimento
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Filtros */}
        <div className="border rounded p-2 mb-3 bg-light">
          <Row className="align-items-end">
            <Col xs={12} sm={4} className="mb-2">
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>GE</Form.Label>
              <Select
                options={opcoesGe}
                value={opcoesGe.find((o) => o.value === filtroGe) || null}
                onChange={(opt) => { setFiltroGe(opt ? opt.value : null); setFiltroConta(null); }}
                isClearable
                placeholder="Todos"
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>
            <Col xs={12} sm={4} className="mb-2">
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Conta</Form.Label>
              <Select
                options={(contasFiltroLookup || []).map((c) => ({ value: c.codigo_conta, label: `${c.codigo_conta} - ${c.apelido}` }))}
                value={(() => {
                  const c = (contasFiltroLookup || []).find((x) => x.codigo_conta === filtroConta);
                  return c ? { value: c.codigo_conta, label: `${c.codigo_conta} - ${c.apelido}` } : null;
                })()}
                onChange={(opt) => setFiltroConta(opt ? opt.value : null)}
                isDisabled={!filtroGe}
                isClearable
                placeholder={!filtroGe ? 'Selecione um GE primeiro' : 'Todas'}
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>
            <Col xs={12} sm={4} className="mb-2">
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Cidade</Form.Label>
              <Select
                options={opcoesCidade}
                value={cidadeFiltroOpcao}
                onChange={(opt) => {
                  setCidadeFiltroOpcao(opt || null);
                  setFiltroCidade(opt ? opt.value : null);
                }}
                onMenuOpen={handleCidadeMenuOpen}
                onInputChange={handleCidadeSearch}
                onMenuScrollToBottom={carregarMaisCidades}
                filterOption={null}
                isLoading={carregandoCidades}
                isClearable
                placeholder="Todas"
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end flex-wrap" style={{ gap: 8 }}>
            <Button variant="primary" onClick={buscar} disabled={carregando}>
              <FaSearch className="mr-1" /> Buscar
            </Button>
            <Button variant="secondary" onClick={limpar} disabled={carregando}>
              <FaEraser className="mr-1" /> Limpar
            </Button>
            <Button variant="success" onClick={() => setMostrarFormIncluir((v) => !v)}>
              <FaPlus className="mr-1" /> Incluir
            </Button>
          </div>
        </div>

        {mostrarFormIncluir && (
          <Formik
            initialValues={{ ge: null, conta: null, cidade: null }}
            validationSchema={schema}
            onSubmit={handleIncluir}
          >
            {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
              <Form onSubmit={handleSubmit} className="border rounded p-2 mb-3" style={{ backgroundColor: '#f8fbff' }}>
                <strong style={{ fontSize: 13 }}>Nova Conta</strong>
                <Row className="mt-2 align-items-end">
                  <Col xs={12} sm={4} className="mb-2">
                    <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                      GE<span className="text-danger"> *</span>
                    </Form.Label>
                    <Select
                      options={opcoesGe}
                      value={opcoesGe.find((o) => o.value === values.ge) || null}
                      onChange={(opt) => {
                        setFieldValue('ge', opt ? opt.value : null);
                        setFieldValue('conta', null);
                        if (opt) lookupContasPorGE(opt.value).then((res) => setContasLookup(res || []));
                        else setContasLookup([]);
                      }}
                      isClearable
                      placeholder="Selecione..."
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                    />
                    {touched.ge && errors.ge && <div className="text-danger" style={{ fontSize: 12 }}>{errors.ge}</div>}
                  </Col>
                  <Col xs={12} sm={4} className="mb-2">
                    <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                      Conta<span className="text-danger"> *</span>
                    </Form.Label>
                    <Select
                      options={(contasLookup || []).map((c) => ({ value: c.codigo_conta, label: `${c.codigo_conta} - ${c.apelido}` }))}
                      value={(() => {
                        const c = (contasLookup || []).find((x) => x.codigo_conta === values.conta);
                        return c ? { value: c.codigo_conta, label: `${c.codigo_conta} - ${c.apelido}` } : null;
                      })()}
                      onChange={(opt) => setFieldValue('conta', opt ? opt.value : null)}
                      isDisabled={!values.ge}
                      isClearable
                      placeholder={!values.ge ? 'Selecione um GE primeiro' : 'Selecione...'}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                    />
                    {touched.conta && errors.conta && <div className="text-danger" style={{ fontSize: 12 }}>{errors.conta}</div>}
                  </Col>
                  <Col xs={12} sm={4} className="mb-2">
                    <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                      Cidade<span className="text-danger"> *</span>
                    </Form.Label>
                    <Select
                      options={opcoesCidade}
                      value={cidadeInclusaoOpcao}
                      onChange={(opt) => {
                        setCidadeInclusaoOpcao(opt || null);
                        setFieldValue('cidade', opt ? opt.value : null);
                      }}
                      onMenuOpen={handleCidadeMenuOpen}
                      onInputChange={handleCidadeSearch}
                      onMenuScrollToBottom={carregarMaisCidades}
                      filterOption={null}
                      isLoading={carregandoCidades}
                      isClearable
                      placeholder="Selecione..."
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                    />
                    {touched.cidade && errors.cidade && <div className="text-danger" style={{ fontSize: 12 }}>{errors.cidade}</div>}
                  </Col>
                </Row>
                <div className="d-flex justify-content-end flex-wrap" style={{ gap: 8 }}>
                  <Button variant="success" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Cadastrar'}
                  </Button>
                  <Button variant="light" onClick={() => setMostrarFormIncluir(false)} disabled={isSubmitting}>
                    Cancelar
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {/* Grid */}
        {carregando ? (
          <div className="text-center py-3"><Spinner animation="border" variant="primary" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 0 }}>
              <thead>
                <tr>
                  <th style={{ color: '#001f5b' }}>GE</th>
                  <th style={{ color: '#001f5b' }}>Conta</th>
                  <th style={{ color: '#001f5b' }}>Descrição</th>
                  <th style={{ color: '#001f5b' }}>Cidade</th>
                  <th style={{ color: '#001f5b' }}>Data Cadastro</th>
                  <th style={{ color: '#001f5b' }}>Cadastrado Por</th>
                  <th style={{ color: '#001f5b', width: 60, textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {contas.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-3">Nenhuma conta cadastrada.</td>
                  </tr>
                ) : (
                  contas.map((c) => (
                    <tr key={`${c.grupo_empresa}-${c.codigo_conta}`}>
                      <td>{c.grupo_empresa}</td>
                      <td>{c.codigo_conta}</td>
                      <td>{c.descricao_conta}</td>
                      <td>{c.descricao_cidade}</td>
                      <td>{c.data_inclusao ? moment(c.data_inclusao).format('DD/MM/YYYY') : '—'}</td>
                      <td>{c.usuario_inclusao || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          variant="link"
                          style={{ padding: 2, color: '#c0392b' }}
                          onClick={() => handleExcluir(c)}
                          title="Excluir"
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="d-inline-flex align-items-center" onClick={onHide}>
          <FaWindowClose />
          <span className="ml-2">Fechar</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalCadContaPrefeitura;
